import type { ISettingValueMaster } from "@bbj-types/setting";
import { IconFromName } from "@data-display/icon/IconSVG";
import { Autocomplete, type AutocompleteProps, Stack, Typography } from "@mui/material";
import { autocompleteSourcesFilterOptions } from "@root/utils/components/autocomplete";
import { useMemo } from "react";

export const defaultGroupsGetValue = (option: Pick<ISettingValueMaster, "group" | "icon">) => option.group;

export const defaultGroupsSort = <Option extends Pick<ISettingValueMaster, "label" | "value" | "group" | "icon">>(
  a: Option,
  b: Option,
  groupsGetValue: (option: Option) => string | undefined
) => {
  const aGroup = groupsGetValue(a);
  const bGroup = groupsGetValue(b);
  if (aGroup === bGroup) {
    return 0;
  }
  if (aGroup === undefined) {
    return 1;
  }
  if (bGroup === undefined) {
    return -1;
  }
  return aGroup.localeCompare(bGroup);
};

export interface IAutocompleteSettingsProps<
  Value extends Pick<ISettingValueMaster, "label" | "value" | "group" | "icon">,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = "div",
> extends AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent> {
  groupsGetValue?: (option: Value) => string | undefined;
  groupsSort?: (a: Value, b: Value, groupsGetValue: (option: Value) => string | undefined) => number;
  showIcons?: boolean;
}

export const AutocompleteSettings = <
  Value extends Pick<ISettingValueMaster, "label" | "value" | "group" | "icon">,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = "div",
>({
  options,
  groupsGetValue = defaultGroupsGetValue,
  groupsSort = defaultGroupsSort,
  showIcons,
  ...props
}: IAutocompleteSettingsProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>) => {
  const hasGroups = useMemo(() => options.some(groupsGetValue), [options, groupsGetValue]);
  const sortedOptions = useMemo(
    () => (hasGroups ? [...options].sort((a, b) => groupsSort(a, b, groupsGetValue)) : options),
    [hasGroups, options, groupsGetValue, groupsSort]
  );

  return (
    <Autocomplete
      {...(hasGroups && {
        groupBy: (v: Value) => groupsGetValue(v) ?? "Sans Catégorie",
      })}
      filterOptions={autocompleteSourcesFilterOptions}
      options={sortedOptions}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      {...(showIcons && {
        renderOption: (props, option: Value) => (
          <Stack
            component="li"
            {...props}
            key={option.value}
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
              whiteSpace: "nowrap",
            }}
          >
            <IconFromName icon={option.icon} color="primary" />

            <Typography variant="body1">{option.label}</Typography>
          </Stack>
        ),
      })}
      {...props}
    />
  );
};
